import { FreeShippingPrompt } from "@/components/cart/FreeShippingPrompt";
import { ShippingSelector } from "@/components/cart/ShippingSelector";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { useFreeShippingMeter, useShippingMethodSync } from "@/queries/cart";
import { collapseProvenance } from "@/utils/cart";
import { formatPrice } from "@/utils/money";
import { getEffectiveShippingSelection } from "@/utils/shippingOption";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo, useMemo } from "react";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface ShippingMethodSectionProps {
  /** Carts to show shipping options for */
  carts: StoreSellerCart[];
  /** Whether the section is disabled */
  disabled?: boolean;
  /** Free shipping threshold in cents (optional) */
  freeShippingThreshold?: number;
}

/**
 * Shipping method selection section for checkout.
 *
 * Displays each seller's cart with:
 * - Seller name
 * - Free shipping prompt (if applicable)
 * - Shipping method dropdown
 *
 * Also shows total shipping cost at the bottom.
 *
 * Features:
 * - Auto-selects shipping method when only one option is available
 * - Clears invalid selection when options change (e.g., after address changes)
 */
export const ShippingMethodSection = memo(function ShippingMethodSection({
  carts,
  disabled = false
}: ShippingMethodSectionProps) {
  // Calculate total shipping
  const totalShipping = useMemo(() => {
    return carts.reduce(
      (sum, cart) => sum + (cart.totals.shipping_total || 0),
      0
    );
  }, [carts]);

  // Across sellers, so absent values are skipped rather than counted against:
  // an internal supplier in the basket must not grey-mark a freight total the
  // direct-integration ones did quote live (KAN-105 AC5/AC9).
  const shippingProvenance = useMemo(
    () =>
      collapseProvenance(
        carts.map((cart) => cart.shipping_methods?.[0]?.provenance)
      ),
    [carts]
  );

  return (
    <div className="space-y-4">
      {/* Per-seller shipping rows */}
      {carts.map((cart) => (
        <SellerShippingRow key={cart.cart_id} cart={cart} disabled={disabled} />
      ))}

      {/* Total shipping cost */}
      <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-gray-700">
          Total Shipping Cost
          <LiveProvenanceBadge
            provenance={shippingProvenance}
            subject="shipping rate"
            source="your suppliers"
          />
        </span>
        <span className="text-base font-semibold text-gray-900">
          {formatPrice(totalShipping)}
        </span>
      </div>
    </div>
  );
});

/**
 * Single seller shipping row with dropdown
 */
interface SellerShippingRowProps {
  cart: StoreSellerCart;
  disabled?: boolean;
}

const SellerShippingRow = memo(function SellerShippingRow({
  cart,
  disabled = false
}: SellerShippingRowProps) {
  // The hook lines the persisted method up against the option list - by its
  // composite live-rate id or its bare base id, the same rule the server keeps
  // it by (KAN-108, KAN-230).
  const {
    options,
    selectedOptionId,
    isLoading,
    isPending,
    isSelectionValid,
    selectOption
  } = useShippingMethodSync({
    cartId: cart.cart_id,
    selectedMethod: cart.shipping_methods[0],
    rearmAutoSelect: true
  });

  // The same meter, and so the same copy, as the cart page: scoped to the
  // service the buyer has selected, and naming it and what it saves.
  const {
    freeShippingThreshold,
    amountToFreeShipping,
    freeShippingSavings,
    freeShippingOptionName
  } = useFreeShippingMeter({
    cartId: cart.cart_id,
    itemSubtotal: cart.totals.item_subtotal,
    selectedShippingOptionId: cart.shipping_methods[0]?.shipping_option_id
  });

  return (
    // Phone: name and badges stack above a full-width selector. From sm the
    // name/badges column takes a fixed width on the left and the selector
    // fills the rest, so the rows line up down the list.
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 sm:w-72 sm:flex-shrink-0">
        {/* Seller name - kept to one line. It truncates rather than wrapping,
            and the full name is on the tooltip when cut short. */}
        <span
          className="min-w-0 max-w-full truncate whitespace-nowrap text-sm font-medium text-gray-900"
          title={cart.seller.name}
        >
          {cart.seller.name}
        </span>

        {/* Whether the *selected* rate - the one already on the cart, whose
          amount feeds the totals - came back live from this supplier, or is
          SalonStocker's own estimate. Not a claim about the options in the
          picker to the right: those come from a different endpoint and carry
          no provenance at all, so an unchosen option is unmarked by nature.

          Ahead of the free-shipping prompt and in a slot wide enough for the
          longer "Estimated" label, held open even when there is no marker.
          Both are conditional, so without a reserved slot each row starts its
          badges at a different x and neither column reads down the list -
          which is the whole point of a per-supplier signal. `w-20` because
          "Estimated" at `text-[10px]` plus dot, gap and padding measures a few
          px past `w-16`, which would defeat the reserved slot it sits in. */}
        <div className="flex-shrink-0 sm:w-20">
          <LiveProvenanceBadge
            provenance={cart.shipping_methods?.[0]?.provenance}
            subject="shipping rate"
            source={cart.seller.name}
          />
        </div>

        {/* Free shipping prompt. Each supplier's note is its own live region:
            it announces a change to that supplier's threshold, and nothing else
            on the page repeats it (the order summary carries no meter here). */}
        {amountToFreeShipping && (
          <div className="min-w-0 max-w-full flex-shrink-0">
            <FreeShippingPrompt
              amountToFreeShipping={amountToFreeShipping}
              freeShippingThreshold={freeShippingThreshold}
              shippingSavings={freeShippingSavings}
              shippingOptionName={freeShippingOptionName}
            />
          </div>
        )}
      </div>

      {/* The wrapper fills the row so justify-end lands the selector on the
          row's right edge, in line with the Total Shipping Cost value. The
          400px cap sits on the selector, not here: capped on the wrapper, it
          would end 400px after the name column and stop short of that edge.
          w-full keeps every row's selector the same width instead of
          shrinking to its text, so the left edges line up down the list.
          min-w-0 lets it shrink on a phone instead of forcing a sideways scroll. */}
      <div className="w-full min-w-0 sm:flex sm:flex-1 sm:justify-end">
        <ShippingSelector
          className="w-full min-w-0 sm:max-w-[400px]"
          options={options}
          isLoading={isLoading}
          isPending={isPending}
          isSelectionValid={isSelectionValid}
          selectedOptionId={selectedOptionId}
          onSelect={selectOption}
          // No effective selection is the required field's error state here
          // (unlike the cart page, which only ever wants the placeholder
          // fallback, never the red border).
          error={
            !getEffectiveShippingSelection(isSelectionValid, selectedOptionId)
          }
          disabled={disabled}
          ariaLabel={`Select shipping method for ${cart.seller.name}`}
        />
      </div>
    </div>
  );
});
